/* iter-p3-x — Tiny word-level diff renderer for the Voice Rewrite dialog.
 *
 * Longest-common-subsequence over whitespace-split tokens.  Not
 * production-grade for huge passages, but plenty for the last 1-3
 * paragraphs we rewrite.  Returns JSX with `<del>` and `<ins>` spans
 * so a writer can see every dialect + register swap at a glance.
 */
import React from 'react';

function tokenize(s) {
  // Split on whitespace boundaries, keeping runs of punctuation
  // attached so a comma or period doesn't render as its own diff hit.
  return (s || '').match(/\S+|\s+/g) || [];
}

function lcs(a, b) {
  const n = a.length, m = b.length;
  const dp = Array.from({ length: n + 1 }, () => new Uint16Array(m + 1));
  for (let i = 1; i <= n; i++) {
    for (let j = 1; j <= m; j++) {
      dp[i][j] = a[i - 1] === b[j - 1]
        ? dp[i - 1][j - 1] + 1
        : Math.max(dp[i - 1][j], dp[i][j - 1]);
    }
  }
  const ops = [];
  let i = n, j = m;
  while (i > 0 && j > 0) {
    if (a[i - 1] === b[j - 1]) { ops.push({ op: '=', v: a[i - 1] }); i--; j--; }
    else if (dp[i - 1][j] >= dp[i][j - 1]) { ops.push({ op: '-', v: a[i - 1] }); i--; }
    else { ops.push({ op: '+', v: b[j - 1] }); j--; }
  }
  while (i > 0) { ops.push({ op: '-', v: a[--i] }); }
  while (j > 0) { ops.push({ op: '+', v: b[--j] }); }
  return ops.reverse();
}

export default function WordDiff({ before, after }) {
  const ops = lcs(tokenize(before), tokenize(after));
  return (
    <div className="whitespace-pre-wrap font-serif text-[12.5px] leading-relaxed"
         data-testid="word-diff">
      {ops.map((o, i) => {
        if (o.op === '=') return <React.Fragment key={i}>{o.v}</React.Fragment>;
        if (o.op === '-') return (
          <del key={i} className="bg-rose-100 px-0.5 text-rose-900 line-through decoration-rose-400"
               data-testid="word-diff-del">{o.v}</del>
        );
        return (
          <ins key={i} className="bg-emerald-100 px-0.5 text-emerald-900 no-underline"
               data-testid="word-diff-ins">{o.v}</ins>
        );
      })}
    </div>
  );
}
